import { Grid, IconButton, Tooltip } from '@material-ui/core';
import React from 'react';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { FieldLabel, FieldValue, StatusPill, ProgressTrack, ProgressFill, HeaderActions } from './blocks';

const HeaderField = ({ label, children }) => (
    <Grid
        item
        css={css`
            display: flex;
            flex-direction: column;
            margin-right: 3rem;
        `}
    >
        <FieldLabel>{label}</FieldLabel>
        <FieldValue
            css={css`
                margin-top: 2px;
            `}
        >
            {children}
        </FieldValue>
    </Grid>
);

// Presentational grid header. All values are already resolved by the container from the header config:
//   { title, subtitle, statusPill:{label,color}|null, fields:[{label,value}],
//     actions:[{key,icon,color,ariaLabel,tooltip?,onClick}], progress:[{label,verb,value,total}]|{...}|null }
// Each action icon is wrapped in a Tooltip (tooltip text, falling back to ariaLabel).
const GridHeader = ({ title, subtitle, statusPill, fields = [], actions = [], progress = null }) => {
    const bars = Array.isArray(progress) ? progress : progress ? [progress] : [];

    return (
        <div
            css={css`
                padding: 1.25rem 1.5rem;
                border-bottom: 1px solid ${colors.greys.border};
            `}
        >
            <Grid
                container
                direction="row"
                css={css`
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: nowrap;
                `}
            >
                <Grid
                    item
                    css={css`
                        display: flex;
                        align-items: center;
                    `}
                >
                    <span
                        css={css`
                            font-size: 18px;
                            font-weight: 800;
                        `}
                    >
                        {title}
                    </span>
                    {actions.length ? (
                        <HeaderActions
                            css={css`
                                margin-left: 16px;
                            `}
                        >
                            {actions.map((action) => {
                                const Icon = action.icon;
                                return (
                                    <Tooltip key={action.key} title={action.tooltip || action.ariaLabel || ''}>
                                        <IconButton size="small" onClick={action.onClick} aria-label={action.ariaLabel}>
                                            <Icon
                                                fontSize="small"
                                                css={css`
                                                    color: ${action.color || colors.greys.primary};
                                                `}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                );
                            })}
                        </HeaderActions>
                    ) : null}
                </Grid>
                {statusPill ? (
                    <Grid item>
                        <StatusPill color={statusPill.color}>{statusPill.label}</StatusPill>
                    </Grid>
                ) : null}
            </Grid>

            {subtitle ? (
                <div
                    css={css`
                        margin-top: 4px;
                        color: ${colors.greys.secondary};
                        font-size: 13px;
                    `}
                >
                    {subtitle}
                </div>
            ) : null}

            {fields.length ? (
                <Grid
                    container
                    direction="row"
                    css={css`
                        margin-top: 1rem;
                        align-items: flex-start;
                    `}
                >
                    {fields.map((field) => (
                        <HeaderField key={field.label} label={field.label}>
                            {field.value}
                        </HeaderField>
                    ))}
                </Grid>
            ) : null}

            {bars.length ? (
                <Grid
                    container
                    direction="column"
                    css={css`
                        margin-top: 1rem;
                        width: 40%;
                        min-width: 260px;
                    `}
                >
                    {bars.map((bar) => {
                        const percent = bar.total > 0 ? (bar.value / bar.total) * 100 : 0;
                        return (
                            <div
                                key={bar.label}
                                css={css`
                                    margin-bottom: 0.75rem;
                                    &:last-of-type {
                                        margin-bottom: 0;
                                    }
                                `}
                            >
                                <Grid
                                    container
                                    direction="row"
                                    css={css`
                                        justify-content: space-between;
                                        align-items: center;
                                        margin-bottom: 4px;
                                    `}
                                >
                                    <FieldLabel>{bar.label}</FieldLabel>
                                    <span
                                        css={css`
                                            font-size: 13px;
                                            font-weight: 700;
                                            color: ${colors.greens.primary};
                                        `}
                                    >
                                        {bar.value} of {bar.total} {bar.verb}
                                    </span>
                                </Grid>
                                <ProgressTrack>
                                    <ProgressFill percent={percent} />
                                </ProgressTrack>
                            </div>
                        );
                    })}
                </Grid>
            ) : null}
        </div>
    );
};

export default GridHeader;
